import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { TASKS_KEY } from './types';

/** GET /api/company-tasks/:id/deliveries (owner 2026-10-24 01:38: "kiedy wysłane i czy odebrane"). */
export type NoticeKind = 'assigned' | 'due' | 'reminder' | 'repeat';
export type DeliveryChannel = 'in_app' | 'push' | 'sms' | 'email';
export type Notice = {
  id: string;
  kind: NoticeKind;
  at: string;
  channels: Array<{ channel: DeliveryChannel; status: 'queued' | 'sent' | 'failed'; sentAt: string | null }>;
  status: 'przeczytane' | 'odebrane' | 'nieodebrane';
  receivedAt: string | null;
  readAt: string | null;
  repeatNo: number | null;
};
export type Deliveries = {
  assigneeUserId: string | null;
  notices: Notice[];
  answer: { kind: 'accepted' | 'done' | null; at: string | null };
  repeats: { sent: number; max: number; nextAt: string | null };
};

export const deliveriesKey = (taskId: string) => [...TASKS_KEY, 'deliveries', taskId];

const KIND: Record<NoticeKind, string> = { assigned: 'Nowe zadanie', due: 'W dniu terminu', reminder: 'Przypomnienie', repeat: 'Ponowne przypomnienie' };
const CHANNEL: Record<DeliveryChannel, string> = { in_app: 'dzwonek', push: 'push', sms: 'SMS', email: 'e-mail' };

const DAY_TIME = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const TIME = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', hour: '2-digit', minute: '2-digit' });
const DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit' });
/** "23.10 21:14" (Warsaw). */
export const dayTime = (iso: string) => DAY_TIME.format(new Date(iso)).replace(',', '');
/** "21:20" on the same Warsaw day as `since`, else "24.10 07:05". */
const later = (iso: string, since: string) => (DAY.format(new Date(iso)) === DAY.format(new Date(since)) ? TIME.format(new Date(iso)) : dayTime(iso));

/** "Nowe zadanie: wysłane 23.10 21:14 (dzwonek, push) · przeczytane 21:20" — a channel waiting or failed says so. */
export function noticeLine(n: Notice): string {
  const kind = n.kind === 'repeat' && n.repeatNo ? `${KIND.repeat} ${n.repeatNo}` : KIND[n.kind];
  const channels = n.channels.map((c) => `${CHANNEL[c.channel]}${c.status === 'queued' ? ' — w kolejce' : c.status === 'failed' ? ' — nie wysłano' : ''}`);
  const sent = `wysłane ${dayTime(n.at)}${channels.length > 0 ? ` (${channels.join(', ')})` : ''}`;
  const back = n.readAt ? `przeczytane ${later(n.readAt, n.at)}` : n.receivedAt ? `odebrane ${later(n.receivedAt, n.at)}` : 'nieodebrane';
  return `${kind}: ${sent} · ${back}`;
}

/** "Odpowiedź: przyjęte 23.10 21:30" / "zrobione …" / "brak". */
export function answerLine(a: Deliveries['answer']): string {
  if (!a.kind || !a.at) return 'Odpowiedź: brak';
  return `Odpowiedź: ${a.kind === 'done' ? 'zrobione' : 'przyjęte'} ${dayTime(a.at)}`;
}

/** "Ponowne przypomnienia: 1 z 3, następne 24.10 07:00", or null when none was sent and none is planned. */
export function repeatsLine(r: Deliveries['repeats']): string | null {
  if (r.sent === 0 && !r.nextAt) return null;
  return `Ponowne przypomnienia: ${r.sent} z ${r.max}${r.nextAt ? `, następne ${dayTime(r.nextAt)}` : ''}`;
}

/**
 * "Powiadomienia" in the task dialog: what went to the person responsible (newest first), whether it came back and
 * their answer. For the person themself and whoever sees who is responsible (the server checks again, rule 7a).
 */
export function TaskDeliveries({ taskId }: { taskId: string }) {
  const q = useQuery({ queryKey: deliveriesKey(taskId), retry: false, queryFn: () => api<Deliveries>(`/api/company-tasks/${taskId}/deliveries`) });
  if (q.isError) return null;
  const repeats = q.data ? repeatsLine(q.data.repeats) : null;
  return (
    <section className="ct-deliveries" aria-labelledby={`ct-del-${taskId}`} data-feature-id="company-tasks.deliveries">
      <h3 id={`ct-del-${taskId}`}>Powiadomienia</h3>
      {q.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {q.data && (
        <>
          {q.data.notices.length === 0 ? (
            <p className="muted">Nic jeszcze nie wysłano.</p>
          ) : (
            <ul className="ct-del-list" aria-label="Wysłane powiadomienia">
              {q.data.notices.map((n) => (
                <li key={n.id} className={`ct-del-${n.status}`}>
                  {noticeLine(n)}
                </li>
              ))}
            </ul>
          )}
          <p className="ct-del-answer">{answerLine(q.data.answer)}</p>
          {repeats && <p className="muted">{repeats}</p>}
        </>
      )}
    </section>
  );
}
